import type { ZplPrinterListItem } from "./zplPrinterDisplay";

export type ZplLabelRequirement = {
  labelSize: string;
  /** When 0, any DPI at that station and stock matches. */
  dpi: number;
};

export type PublicStationOption = {
  id: number;
  name: string;
  siteCode: string | null;
};

const LEGACY_STATION_NAME: Record<string, string> = {
  Inv: "Inventory",
  Rec: "Receiving",
  Ship: "Shipping",
};

export function zplPrintStationLabel(stationId: string, stations: readonly PublicStationOption[]): string {
  const id = Number(stationId);
  const station = stations.find((row) => row.id === id);
  if (station == null) return "this location";
  return station.name;
}

export function isZplPrintStationId(value: string | null | undefined): boolean {
  const trimmed = value?.trim() ?? "";
  return /^[0-9]+$/.test(trimmed);
}

export type ZplPrinterResolution = {
  printerName: string;
  unavailableMessage: string | null;
};

function stockMatches(printer: ZplPrinterListItem, labelSize: string): boolean {
  const wanted = labelSize.trim().toLowerCase();
  const actual = printer.labelSize?.trim().toLowerCase() ?? "";
  return wanted !== "" && actual === wanted;
}

export function resolveZplPrinterAtStation(
  printers: readonly ZplPrinterListItem[],
  stationId: string,
  requirement: ZplLabelRequirement,
): ZplPrinterResolution {
  const id = Number(stationId);
  if (!Number.isInteger(id)) {
    return { printerName: "", unavailableMessage: "Pick a location." };
  }
  const labelSize = requirement.labelSize.trim();
  const dpi = requirement.dpi;
  const matches = printers.filter(
    (printer) =>
      printer.stationId === id &&
      stockMatches(printer, labelSize) &&
      (dpi === 0 || printer.dpi === dpi),
  );
  if (matches.length === 0) {
    return {
      printerName: "",
      unavailableMessage: `No ${labelSize} printer at that location.`,
    };
  }
  const online = matches.find((printer) => printer.online !== false);
  const chosen = online ?? matches[0]!;
  if (chosen.online === false) {
    const name = chosen.displayName?.trim() || chosen.identifier?.trim() || chosen.name;
    return { printerName: "", unavailableMessage: `${name} is offline.` };
  }
  return { printerName: chosen.name, unavailableMessage: null };
}

export function stationIdFromStoredPrinter(
  stored: string,
  stations: readonly PublicStationOption[],
  printers: readonly ZplPrinterListItem[] | undefined,
): string {
  const trimmed = stored.trim();
  if (isZplPrintStationId(trimmed) && stations.some((station) => String(station.id) === trimmed)) {
    return trimmed;
  }
  const legacyName = LEGACY_STATION_NAME[trimmed];
  if (legacyName != null) {
    const station = stations.find((row) => row.name === legacyName && (row.siteCode == null || row.siteCode === "sgs"));
    if (station != null) return String(station.id);
  }
  const match = printers?.find((printer) => printer.name === trimmed || printer.identifier === trimmed);
  if (match?.stationId != null) return String(match.stationId);
  return "";
}
